import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mail, Lock, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { API, ASSETS } from "@/lib/gp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

async function post(path, body) {
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Something went wrong.");
  return data;
}

export default function Auth() {
  const resetToken = new URLSearchParams(window.location.search).get("reset");
  const verifyToken = new URLSearchParams(window.location.search).get("verify");
  const [mode, setMode] = useState(resetToken ? "reset" : "login");
  const [form, setForm] = useState({ email: "", password: "", confirm: "", name: "", website: "" });
  const [registerStartedAt, setRegisterStartedAt] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const { login, register, startGoogle, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dest = location.state?.from || "/chat";
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!verifyToken) return;
    post("/auth/verify-email", { token: verifyToken })
      .then((data) => {
        toast.success(data.message || "Email verified.");
        window.history.replaceState(null, "", "/login");
        return refreshUser();
      })
      .catch((err) => toast.error(String(err.message || err)));
  }, [verifyToken, refreshUser]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "forgot") {
        const data = await post("/auth/forgot-password", { email: form.email });
        toast.success(data.message);
        setMode("login");
        return;
      }
      if (mode === "reset") {
        if (form.password !== form.confirm) throw new Error("The new passwords do not match.");
        const data = await post("/auth/reset-password", { token: resetToken, password: form.password });
        toast.success(data.message);
        window.history.replaceState(null, "", "/login");
        setForm({ email: "", password: "", confirm: "", name: "" });
        setMode("login");
        return;
      }
      const u = mode === "login"
        ? await login(form.email, form.password)
        : await register(form.email, form.password, form.name, { website: form.website, started_at: registerStartedAt });
      toast.success(mode === "login" ? "Welcome back!" : "Account created — check your email to verify.");
      navigate(u?.is_admin ? "/admin" : dest, { replace: true });
    } catch (err) {
      toast.error(String(err.message || err));
    } finally {
      setBusy(false);
    }
  };

  const title = mode === "login" ? "Welcome back" : mode === "register" ? "Join GamePlat" : mode === "forgot" ? "Reset password" : "Choose new password";
  const description = mode === "forgot"
    ? "Enter your account email. We will send a secure link valid for 30 minutes."
    : mode === "reset"
      ? "Choose a new password with at least eight characters."
      : mode === "login"
        ? "Sign in to keep your Yorick chats and plan on every device."
        : "Create an account to sync your chats and plan everywhere.";

  return (
    <div className="mx-auto max-w-md px-5 py-16 md:py-24">
      <div className="glass rounded-3xl p-8">
        <div className="flex flex-col items-center text-center">
          <img src={ASSETS.avatar} alt="Yorick" className="h-16 w-16 rounded-2xl object-cover ring-1 ring-gp-blue/40" />
          <h1 className="mt-4 font-unbounded font-black tracking-tighter text-3xl uppercase">{title}</h1>
          <p className="text-white/60 mt-2 text-sm">{description}</p>
        </div>

        {(mode === "login" || mode === "register") && (
          <>
            <button onClick={() => startGoogle(dest)} data-testid="google-signin" className="mt-7 w-full flex items-center justify-center gap-3 rounded-full bg-white text-gray-900 font-semibold py-3 hover:bg-white/90 transition-colors">
              <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.7 1.22 9.19 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
              Continue with Google
            </button>
            <div className="my-6 flex items-center gap-3 text-white/30 text-xs"><span className="h-px flex-1 bg-white/10" /> OR <span className="h-px flex-1 bg-white/10" /></div>
          </>
        )}

        <form onSubmit={submit} className="space-y-3 mt-6" data-testid="auth-form">
          {mode === "register" && (
            <div className="absolute -left-[10000px] h-px w-px overflow-hidden" aria-hidden="true">
              <label htmlFor="website">Website</label>
              <input id="website" name="website" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" />
            </div>
          )}
          {mode === "register" && (
            <div className="relative"><User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" /><Input value={form.name} onChange={set("name")} placeholder="Display name" className="pl-10 bg-black/30 border-white/10 h-12 rounded-xl" /></div>
          )}
          {mode !== "reset" && (
            <div className="relative"><Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" /><Input type="email" value={form.email} onChange={set("email")} placeholder="Email" required className="pl-10 bg-black/30 border-white/10 h-12 rounded-xl" /></div>
          )}
          {mode !== "forgot" && (
            <div className="relative"><Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" /><Input type="password" value={form.password} onChange={set("password")} placeholder={mode === "reset" ? "New password" : "Password"} minLength={mode === "login" ? 1 : 8} required className="pl-10 bg-black/30 border-white/10 h-12 rounded-xl" /></div>
          )}
          {mode === "reset" && (
            <div className="relative"><Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" /><Input type="password" value={form.confirm} onChange={set("confirm")} placeholder="Confirm new password" minLength={8} required className="pl-10 bg-black/30 border-white/10 h-12 rounded-xl" /></div>
          )}
          <Button type="submit" disabled={busy} className="w-full rounded-full py-6 bg-gp-blue hover:bg-gp-blue/90 text-white font-bold">
            {busy ? <Loader2 className="animate-spin" size={18} /> : mode === "login" ? "Sign in" : mode === "register" ? "Create account" : mode === "forgot" ? "Email reset link" : "Save new password"}
          </Button>
        </form>

        {mode === "login" && (
          <p className="text-center text-sm mt-4">
            <Link to="/forgot-password" className="text-gp-blue font-semibold hover:underline" data-testid="forgot-password-link">
              Forgot your password?
            </Link>
          </p>
        )}
        {(mode === "login" || mode === "register") && (
          <p className="text-center text-sm text-white/50 mt-5">
            {mode === "login" ? "New to GamePlat? " : "Already have an account? "}
            <button
              data-testid="auth-toggle"
              onClick={() => {
                const nextMode = mode === "login" ? "register" : "login";
                if (nextMode === "register") setRegisterStartedAt(Date.now());
                setMode(nextMode);
              }}
              className="text-gp-blue font-semibold hover:underline"
            >
              {mode === "login" ? "Create one" : "Sign in"}
            </button>
          </p>
        )}
      </div>
      <p className="text-center text-white/30 text-xs mt-5">By continuing you agree to our <Link to="/terms" className="underline">Terms</Link> and <Link to="/privacy" className="underline">Privacy</Link>.</p>
    </div>
  );
}
